import { useState, useEffect, useRef } from "react";
import Layout from "./Layout";
import { useRobotStatus, getStatusColor } from "../hooks/useRobotStatus";
import {
  initializePerspectiveTransform,
  applyPerspectiveTransform,
} from "../utils/perspectiveTransform";
import { SITES } from "../sites";
import "./OverallStatusPage.css";

const RobotOverviewCard = ({ site }) => {
  const { robotData } = useRobotStatus(site.deviceId, true);
  const [markerPos, setMarkerPos] = useState(null);
  const [imageMetrics, setImageMetrics] = useState({ scaleX: 1, scaleY: 1 });
  const mapImageRef = useRef(null);
  const transformMatrixRef = useRef(null);

  const status = robotData?.status ?? "OFFLINE";
  const statusColor = getStatusColor(status);

  useEffect(() => {
    transformMatrixRef.current = initializePerspectiveTransform(
      site.perspectiveMapping,
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const updateImageMetrics = () => {
      if (!mapImageRef.current) return;
      const img = mapImageRef.current;
      setImageMetrics({
        scaleX: img.clientWidth / site.mapOriginalSize.width,
        scaleY: img.clientHeight / site.mapOriginalSize.height,
      });
    };

    const imgElement = mapImageRef.current;
    if (imgElement) {
      if (imgElement.complete) updateImageMetrics();
      else imgElement.addEventListener("load", updateImageMetrics);
    }
    window.addEventListener("resize", updateImageMetrics);
    return () => {
      if (imgElement)
        imgElement.removeEventListener("load", updateImageMetrics);
      window.removeEventListener("resize", updateImageMetrics);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!robotData?.location || !transformMatrixRef.current) {
      setMarkerPos(null);
      return;
    }
    const x = parseFloat(robotData.location.x);
    const y = parseFloat(robotData.location.y);
    const theta = parseFloat(robotData.location.theta);
    if (isNaN(x) || isNaN(y) || isNaN(theta)) return;

    const transformed = applyPerspectiveTransform(
      { x, y },
      transformMatrixRef.current,
    );
    setMarkerPos({
      x: transformed.x * imageMetrics.scaleX,
      y: transformed.y * imageMetrics.scaleY,
      deg: -theta + 90 + (site.headingOffset ?? 0),
    });
  }, [robotData, imageMetrics, site.headingOffset]);

  return (
    <div className="robot-overview-card">
      <div className="overview-card-header">
        <span className="overview-site-name">{site.label}</span>
        <div className="overview-robot-status">
          <span className="overview-robot-name">{site.robotName}</span>
          <div className="overview-status-group">
            <span className={`overview-status-dot ${statusColor}`} />
            <span className="overview-status-label">{status}</span>
          </div>
        </div>
      </div>
      <div className="overview-map-container">
        <div className="overview-map-wrapper">
          <img
            ref={mapImageRef}
            src={site.mapImage}
            alt={`${site.label} 지도`}
            className="overview-map-image"
          />
          {markerPos && (
            <div
              className={`robot-marker ${statusColor}`}
              style={{
                left: `${markerPos.x}px`,
                top: `${markerPos.y}px`,
                transform: `translate(-50%, -50%) rotate(${markerPos.deg}deg)`,
              }}
            >
              <div className="marker-aura" />
              <div className="marker-circle" />
              <div className="marker-direction" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OverallStatusPage = () => {
  return (
    <Layout>
      <div className="overall-status-page">
        <div className="robot-overview-grid">
          {SITES.map((site) => (
            <RobotOverviewCard key={site.deviceId} site={site} />
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default OverallStatusPage;
